import type { Card } from '@shared/types/game';
import { SUIT_SYMBOLS, isRedSuit, rankLabel } from '@shared/game-logic';

type Size = 'sm' | 'md' | 'lg';

/**
 * Cards grow at the tablet breakpoint. A hand that is comfortable on a phone is
 * uselessly small on an iPad lying on the table, which is how this actually gets
 * played, so every size has a second step from `md` (768px) up.
 *
 * **The type scales with the box.** Everything on the face is sized in `em`, so a
 * font that is proportionally too large for its card makes the pips collide — and
 * that is exactly what `sm` did: 14px on a 40px-wide card where `md` and `lg` sit
 * at 0.28 of their width. Keep the ratio near 0.28 when adding a size, and
 * measure it (`npm run check:cards`) rather than eyeballing it.
 */
const SIZES: Record<Size, string> = {
  sm: 'h-14 w-10 text-[0.7rem] md:h-20 md:w-14 md:text-[1rem]',
  md: 'h-20 w-14 text-[1rem] md:h-28 md:w-20 md:text-[1.4rem]',
  lg: 'h-24 w-16 text-[1.15rem] md:h-36 md:w-[6.25rem] md:text-[1.8rem]',
};

/**
 * A card face, drawn rather than loaded: no image assets means nothing to fetch
 * on a campsite connection and nothing to precache, and it stays crisp at every
 * size the layout asks for.
 */
export function PlayingCard({
  card,
  size = 'md',
  dimmed = false,
  selected = false,
  onClick,
  disabled = false,
  label,
  className = '',
  style,
}: {
  card: Card;
  size?: Size;
  /** Legal-but-not-playable, e.g. you must follow another suit. */
  dimmed?: boolean;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const red = isRedSuit(card.suit);
  const suit = SUIT_SYMBOLS[card.suit];

  /*
   * Three rows, not three absolutely positioned things.
   *
   * The index in the corner used to be placed by hand and the big pip centred over
   * the whole card, so on the small size the little suit under the rank ran into
   * it — measured at five pixels by eleven. Any fix by numbers is one font change
   * away from colliding again, so the middle row now *owns* the band the big pip
   * sits in and the indices own theirs. They cannot reach each other.
   *
   * `1fr` in the middle, `auto` top and bottom: the indices take what they need and
   * the pip gets the rest, at every size and both breakpoints.
   */
  const index = (
    <>
      <span className="font-bold">{rankLabel(card.rank)}</span>
      <span className="text-[0.55em] leading-none">{suit}</span>
    </>
  );

  const face = (
    <span className="absolute inset-0 grid grid-rows-[auto_1fr_auto] px-1 py-0.5 md:px-1.5 md:py-1">
      <span data-card-index className="flex flex-col items-start leading-none">
        {index}
      </span>
      {/* Centred in its own row, so it has nothing to bump into. */}
      <span
        data-card-pip
        className="flex items-center justify-center text-[1.6em] leading-none opacity-90"
      >
        {suit}
      </span>
      {/* Rotated as a whole block, which mirrors it into the opposite corner and
          keeps the rank on the outside — the way a real card reads upside down. */}
      <span className="flex rotate-180 flex-col items-start leading-none">{index}</span>
    </span>
  );

  const classes = [
    'themed-card relative shrink-0 border bg-gradient-to-br from-white to-slate-100 font-semibold',
    'shadow-[0_1px_2px_rgba(0,0,0,0.4),inset_0_0_0_1px_rgba(255,255,255,0.6)]',
    'transition-transform',
    SIZES[size],
    red ? 'text-red-600' : 'text-slate-900',
    selected ? 'border-accent-500 ring-2 ring-accent-500 -translate-y-2' : 'border-slate-400/60',
    // Greyed out via a filter, not opacity: the deal-in animation ends on
    // `opacity: 1` with fill-mode both, and an animation beats a plain
    // declaration — so an opacity-based dim silently stopped working once a card
    // had finished animating in.
    dimmed ? 'grayscale brightness-[0.55] saturate-0' : '',
    onClick && !disabled ? 'hover:-translate-y-1 active:translate-y-0' : '',
    disabled ? 'cursor-not-allowed' : '',
    className,
  ].join(' ');

  if (!onClick) {
    return (
      <div className={classes} style={style} aria-label={label ?? cardName(card)} role="img">
        {face}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={classes}
      style={style}
      aria-label={label ?? cardName(card)}
    >
      {face}
    </button>
  );
}

/** Face-down card, for a hand you are not allowed to see. */
export function CardBack({ size = 'sm', className = '' }: { size?: Size; className?: string }) {
  return (
    <div
      aria-hidden
      className={[
        // The pattern belongs to the theme — a woven diagonal by default, a
        // real printed back where one was drawn for it. See `.themed-back`.
        'themed-back shrink-0 border border-base-600',
        'shadow-[inset_0_0_0_2px_rgba(255,255,255,0.06)]',
        SIZES[size],
        className,
      ].join(' ')}
    />
  );
}

const SUIT_NAMES: Record<Card['suit'], string> = {
  spades: 'schoppen',
  hearts: 'harten',
  diamonds: 'ruiten',
  clubs: 'klaveren',
};

export function cardName(card: Card): string {
  return `${SUIT_NAMES[card.suit]} ${rankLabel(card.rank)}`;
}
